import React, { useState } from 'react';
import './EventCard.css';
import { Calendar, Crosshair, FilePenLine, MapPin, Trash2, UserRoundPen, Target, Bird, ImageIcon, Images } from 'lucide-react';
import { socialService } from '../../../../../../services';
import ShareMenuButton from '../../../../../../components/share/ShareMenuButton';

const formatEventDate = (value) => {
    if (!value) {
        return 'Date TBC';
    }

    if (typeof value === 'string') {
        const trimmedValue = value.trim();

        if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmedValue)) {
            return trimmedValue;
        }

        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
            const [year, month, day] = trimmedValue.split('-').map(Number);
            const localDate = new Date(year, month - 1, day);

            if (!Number.isNaN(localDate.getTime())) {
                return localDate.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                });
            }
        }
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
        return 'Date TBC';
    }

    return parsedDate.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

const EventCard = ({ event, onEdit, onEditShooters, onDelete, onManageScoreSheets, onQuickFunShoot, onManageMedia }) => {
    const [isDeleting, setIsDeleting] = useState(false);

    const formattedDate = formatEventDate(event.eventDate);

    const normalizedEventType = String(event.eventType || event.EventType || '').trim().toLowerCase();
    const eventType = normalizedEventType === 'rifle'
        ? 'Rifle'
        : normalizedEventType === 'game'
            ? 'Game'
            : 'Clay';
    const isOwner = event.isOwner !== false;
    const isShooter = event.isShooter === true;

    const getEventTypeIcon = () => {
        switch (eventType) {
            case 'Rifle': return <Target size={14} />;
            case 'Game': return <Bird size={14} />;
            default: return <Crosshair size={14} />;
        }
    };

    const getEventTypeClass = () => {
        switch (eventType) {
            case 'Rifle': return 'event-type-rifle';
            case 'Game': return 'event-type-game';
            default: return 'event-type-clay';
        }
    };

    const getCardAccentClass = () => {
        switch (eventType) {
            case 'Rifle': return 'event-card-rifle';
            case 'Game': return 'event-card-game';
            default: return 'event-card-clay';
        }
    };

    const getActionLabel = () => {
        switch (eventType) {
            case 'Rifle': return 'Manage Rifle';
            case 'Game': return 'Manage Game Day';
            default: return 'Score Shoot';
        }
    };

    const handleDelete = async (e) => {
        e.preventDefault();
        if (isDeleting) return;

        setIsDeleting(true);
        try {
            await onDelete();
        } finally {
            setIsDeleting(false);
        }
    };

    const shareOnPlatform = async (platform) => {
        try {
            const platformKey = platform === 'Copy' ? 'CopyLink' : platform;
            const data = await socialService.shareEvent(event.id, platformKey);

            if (!data?.shareUrl) {
                return;
            }

            if (platform === 'Copy') {
                try {
                    await navigator.clipboard.writeText(data.shareUrl);
                } catch {
                    const textArea = document.createElement('textarea');
                    textArea.value = data.shareUrl;
                    document.body.appendChild(textArea);
                    textArea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textArea);
                }
                return;
            }

            window.open(data.shareUrl, '_blank', 'noopener,noreferrer,width=700,height=550');
        } catch (error) {
            console.error('Error sharing event:', error);
        }
    };

    return (
        <div className={`dashboard-card event-card-shell ${getCardAccentClass()}`}>
            <div className="event-card-header">
                <div className="event-card-title-row">
                    <h3 className="dashboard-card-headline event-card-title">{event.name}</h3>
                    <span className={`event-type-badge ${getEventTypeClass()}`}>
                        {getEventTypeIcon()}
                        {eventType}
                    </span>
                </div>
                <div className="event-card-header-actions">
                    {isOwner && (
                        <>
                            <button className='square-action blue-action' onClick={onEdit}>
                                <FilePenLine size={15} />
                            </button>
                            <button
                                className={`square-action red-action ${isDeleting ? 'disabled' : ''}`}
                                disabled={isDeleting}
                                onClick={handleDelete}
                            >
                                <Trash2 size={15} />
                            </button>
                        </>
                    )}
                </div>
            </div>
            <div className="event-card-info">
                {event.coverImageUrl && (
                    <div className="event-cover-wrap">
                        <img src={event.coverImageUrl} alt={`${event.name} cover`} className="event-cover-image" />
                    </div>
                )}
                <h6><Calendar size={18} /> <span>{formattedDate}</span></h6>
                <h6><MapPin size={18} /> <span>{event.clubName}</span></h6>
                {event.imageCount > 0 && (
                    <h6><ImageIcon size={18} /> <span>{event.imageCount} image{event.imageCount === 1 ? '' : 's'}</span></h6>
                )}
                {event.ownerName && (
                    <h6><UserRoundPen size={18} /> <span>{isOwner ? 'You own this event' : `Owner: ${event.ownerName}`}</span></h6>
                )}
                {!isOwner && isShooter && (
                    <div className="event-visibility-note">
                        You can see this event because you were added as a shooter.
                    </div>
                )}
            </div>
            <div className="dashboard-card-cta mt-5">
                <div className="event-card-primary-actions">
                    <button
                        className={`dashboard-card-default-btn event-primary-btn ${getEventTypeClass()}`}
                        onClick={onManageScoreSheets}
                    >
                        <Crosshair size={15} className='me-2' />
                        {isOwner ? getActionLabel() : 'Open Event'}
                    </button>
                </div>
                <div className="event-card-secondary-actions">
                    {isOwner && (
                        <button className="dashboard-card-outline-btn event-secondary-btn" onClick={onEditShooters}>
                            <UserRoundPen size={15} className='me-2' />
                            Shooters
                        </button>
                    )}
                    {(isOwner || isShooter) && (
                        <button className="dashboard-card-outline-btn event-secondary-btn" onClick={onManageMedia || onEdit}>
                            <Images size={15} className='me-2' />
                            Media
                        </button>
                    )}
                    {eventType === 'Clay' && isOwner && (
                        <button
                            className="dashboard-card-outline-btn event-secondary-btn"
                            onClick={onQuickFunShoot}
                        >
                            <Crosshair size={15} className='me-2' />
                            Quick Fun
                        </button>
                    )}
                    <ShareMenuButton className="event-share-wrap" onShare={shareOnPlatform} direction="up" />
                </div>
            </div>
        </div>
    );
};

export default EventCard;
